import type { AppContext } from "./context";
import { AppError } from "../core/errors";
import { el, on, toast } from "./dom";
import {
  forgetSaveFolder,
  getSaveFolder,
  pickSaveFolder,
  supportsFileSystemAccess,
  folderPermission,
  requestFolderPermission,
} from "../store/destination";

export function mountSettingsUi(ctx: AppContext): { refreshFolder: () => Promise<void> } {
  const folderLine = el<HTMLParagraphElement>("folder-line");
  const chooseButton = el<HTMLButtonElement>("choose-folder");
  const forgetButton = el<HTMLButtonElement>("forget-folder");
  const defaultCamera = el<HTMLInputElement>("default-camera");
  const defaultMic = el<HTMLInputElement>("default-mic");
  const defaultSystemAudio = el<HTMLInputElement>("default-system-audio");
  const countdown = el<HTMLSelectElement>("countdown-seconds");
  const envLine = el<HTMLParagraphElement>("env-line");

  async function refreshFolder(): Promise<void> {
    const folder = await getSaveFolder();
    if (!folder) {
      folderLine.textContent = supportsFileSystemAccess()
        ? "No folder chosen — recordings need one."
        : "This browser has no folder access; takes are kept in the app.";
      forgetButton.disabled = true;
      return;
    }
    const state = await folderPermission(folder);
    folderLine.textContent =
      state === "granted"
        ? `Saving to “${folder.name}”.`
        : `“${folder.name}” needs permission again — click Choose folder to re-allow it.`;
    forgetButton.disabled = false;
  }

  on(chooseButton, "click", async () => {
    try {
      const existing = await getSaveFolder();
      if (existing && (await folderPermission(existing)) !== "granted") {
        const granted = await requestFolderPermission(existing);
        if (granted) {
          ctx.session.useFolder(existing);
          await refreshFolder();
          toast(`Saving to “${existing.name}”.`, "ok");
          return;
        }
      }
      const picked = await pickSaveFolder();
      if (picked) {
        // The session holds its own reference; a new folder must replace it or
        // takes keep going to the old one while the UI says otherwise.
        ctx.session.useFolder(picked);
        ctx.settings.folderName = picked.name;
        await ctx.persist();
        await refreshFolder();
        toast(`Saving to “${picked.name}”.`, "ok");
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === "AbortError") return;
      toast(error instanceof AppError ? error.message : "Folder could not be chosen.", "error");
    }
  });

  on(forgetButton, "click", async () => {
    ctx.session.useFolder(null);
    await forgetSaveFolder();
    ctx.settings.folderName = null;
    await ctx.persist();
    await refreshFolder();
  });

  on(defaultCamera, "change", () => {
    ctx.settings.cameraOn = defaultCamera.checked;
    void ctx.persist();
  });
  on(defaultMic, "change", () => {
    ctx.settings.micOn = defaultMic.checked;
    void ctx.persist();
  });
  on(defaultSystemAudio, "change", () => {
    ctx.settings.systemAudioOn = defaultSystemAudio.checked;
    void ctx.persist();
  });
  on(countdown, "change", () => {
    ctx.settings.countdownSeconds = Number(countdown.value) as 0 | 3 | 5;
    void ctx.persist();
  });

  defaultCamera.checked = ctx.settings.cameraOn;
  defaultMic.checked = ctx.settings.micOn;
  defaultSystemAudio.checked = ctx.settings.systemAudioOn;
  countdown.value = String(ctx.settings.countdownSeconds);

  const display = window.screen as Screen & { isExtended?: boolean };
  envLine.textContent = [
    navigator.userAgent.includes("Chrome") ? "Chrome" : navigator.userAgent,
    `screen ${display.width}×${display.height}`,
    `${navigator.hardwareConcurrency} cores`,
    display.isExtended ? "multiple displays detected" : "single display",
  ].join(" · ");

  void refreshFolder();
  return { refreshFolder };
}
